<template>
    <div class="content-wrapper attendance-worksite-page">
        <div class="row align-items-start mb-3">
            <div class="col-xl-6">
                <app-breadcrumb :page-title="$t('attendance_by_worksite')" :icon="'map-pin'" />
                <p class="attendance-worksite-page__intro text-muted mb-0">
                    {{ $t('attendance_by_worksite_intro') }}
                </p>
            </div>
            <div class="col-xl-6 mt-3 mt-xl-0">
                <div class="attendance-worksite-page__top-actions attendance-worksite-page__top-actions--compact">
                    <div class="btn-group attendance-worksite-page__view-switch" role="group" :aria-label="$t('attendance_view_switch')">
                        <a class="btn btn-outline-secondary btn-sm" href="/app-attendances">
                            {{ $t('list_view') }}
                        </a>
                        <button type="button" class="btn btn-dark btn-sm" disabled>
                            {{ $t('map_view') }}
                        </button>
                    </div>

                    <div class="attendance-worksite-page__date-nav attendance-worksite-page__date-nav--compact">
                        <!-- <div class="attendance-worksite-page__date-pill attendance-worksite-page__date-pill--compact">
                            <span>{{ $t('date') }}</span>
                            <strong>{{ formattedSelectedDate }}</strong>
                        </div> -->
                        <input
                            v-model="selectedDate"
                            type="date"
                            class="form-control attendance-worksite-page__date-input attendance-worksite-page__date-input--compact"
                            @change="applyFilters"
                        />
                    </div>

                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="refreshOverview">
                        {{ $t('refresh') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="card card-with-shadow border-0 attendance-worksite-page__map-shell">
            <div class="card-body attendance-worksite-page__map-body position-relative">
                <app-overlay-loader v-if="isLoading" />

                <div class="attendance-worksite-page__map-toolbar">
                    <div class="attendance-worksite-page__toolbar-search">
                        <span class="attendance-worksite-page__toolbar-search-icon-wrap" aria-hidden="true">
                            <app-icon name="search" class="attendance-worksite-page__toolbar-search-icon" />
                        </span>
                        <input
                            v-model.trim="searchQuery"
                            type="text"
                            class="form-control attendance-worksite-page__toolbar-search-input"
                            :placeholder="$t('search')"
                            :aria-label="$t('search')"
                            autocomplete="off"
                            @input="handleSearchInput"
                        />
                    </div>
                    <div class="attendance-worksite-page__map-toolbar-actions">
                        <div class="btn-group btn-group-sm" role="group" :aria-label="$t('map_mode')">
                            <button type="button" class="btn attendance-worksite-page__map-mode-btn" :class="{ 'is-active': mapViewMode === 'roadmap' }" @click="setMapViewMode('roadmap')">
                                {{ $t('map_standard') }}
                            </button>
                            <button type="button" class="btn attendance-worksite-page__map-mode-btn" :class="{ 'is-active': mapViewMode === 'satellite' }" @click="setMapViewMode('satellite')">
                                {{ $t('map_satellite') }}
                            </button>
                            <button type="button" class="btn attendance-worksite-page__map-mode-btn" :class="{ 'is-active': mapViewMode === 'hybrid' }" @click="setMapViewMode('hybrid')">
                                {{ $t('map_hybrid') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="!worksites.length && !isLoading" class="attendance-worksite-page__empty-state">
                    <app-icon name="map-pin" />
                    <h5>{{ $t('attendance_worksite_map_empty_title') }}</h5>
                    <p class="mb-0">{{ $t('attendance_worksite_map_empty_hint') }}</p>
                </div>

                <template v-else>
                    <div ref="map" class="attendance-worksite-page__map"></div>
                </template>
            </div>
        </div>

        <attendance-worksite-drawer
            v-if="drawerWorksite"
            :key="`${drawerWorksite.location.id}-${selectedDate}-${filterSignature}`"
            :location-summary="drawerWorksite"
            :selected-date="selectedDate"
            :filters="requestFilterPayload"
            @close="closeDrawer"
        />
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import AttendanceWorksiteDrawer from './AttendanceWorksiteDrawer.vue';
import { ATTENDANCE_WORKSITE_MAP_DATA } from '../../../Config/ApiUrl';

const DEFAULT_CENTER = { lat: -11.6645, lng: 27.4793 };
const FOCUS_PADDING = 72;
const MAX_FOCUS_ZOOM = 16;
const FALLBACK_GOOGLE_MAPS_KEY = 'AIzaSyA8emh2Pq3wIootXyoX5zILgIqaxxOrs-E';

function createAttendanceWorksiteMarkerOverlay({ google, map, worksite, isSelected, onClick }) {
    if (!google?.maps || !google.maps.OverlayView) {
        return null;
    }

    class AttendanceWorksiteOverlay extends google.maps.OverlayView {
        constructor() {
            super();
            this.position = new google.maps.LatLng(
                Number(worksite.location.center_lat || worksite.location.latitude),
                Number(worksite.location.center_lng || worksite.location.longitude)
            );
            this.container = null;
        }

        onAdd() {
            const container = document.createElement('div');
            container.className = 'attendance-worksite-marker-overlay';
            container.dataset.tone = worksite.tone || 'neutral';

            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'attendance-worksite-marker-button';
            button.setAttribute('aria-label', worksite.location.name || 'Worksite');

            const badge = document.createElement('span');
            badge.className = 'attendance-worksite-marker-badge';

            const badgeDot = document.createElement('span');
            badgeDot.className = 'attendance-worksite-marker-badge-dot';
            const badgeName = document.createElement('span');
            badgeName.className = 'attendance-worksite-marker-badge-name';
            badgeName.textContent = worksite.location.name || 'Worksite';
            badgeName.title = worksite.location.name || 'Worksite';
            const badgeSeparator = document.createElement('span');
            badgeSeparator.className = 'attendance-worksite-marker-badge-separator';
            badgeSeparator.setAttribute('aria-hidden', 'true');
            const badgeCount = document.createElement('span');
            badgeCount.className = 'attendance-worksite-marker-badge-count';
            badgeCount.textContent = String(worksite.summary?.employees_count || 0);

            badge.appendChild(badgeDot);
            badge.appendChild(badgeName);
            badge.appendChild(badgeSeparator);
            badge.appendChild(badgeCount);

            const pinWrap = document.createElement('span');
            pinWrap.className = 'attendance-worksite-marker-pin-wrap';
            const pulse = document.createElement('span');
            pulse.className = 'attendance-worksite-marker-pulse';
            const pin = document.createElement('span');
            pin.className = 'attendance-worksite-marker-pin';

            pinWrap.appendChild(pulse);
            pinWrap.appendChild(pin);

            button.appendChild(badge);
            button.appendChild(pinWrap);
            container.appendChild(button);

            button.addEventListener('click', () => onClick?.(worksite));

            this.container = container;
            this.getPanes().overlayMouseTarget.appendChild(container);
            this.syncClasses();
        }

        draw() {
            if (!this.container) {
                return;
            }

            const projection = this.getProjection();
            const point = projection.fromLatLngToDivPixel(this.position);
            if (!point) {
                return;
            }

            this.container.style.left = `${point.x}px`;
            this.container.style.top = `${point.y}px`;
        }

        onRemove() {
            if (this.container?.parentNode) {
                this.container.parentNode.removeChild(this.container);
            }
            this.container = null;
        }

        syncClasses() {
            if (!this.container) {
                return;
            }

            this.container.classList.toggle('is-selected', isSelected);
            this.container.classList.toggle('is-critical', worksite.tone === 'critical');
            this.container.classList.toggle('is-warning', worksite.tone === 'warning');
            this.container.classList.toggle('is-healthy', worksite.tone === 'healthy');
            this.container.classList.toggle('is-neutral', worksite.tone === 'neutral');
        }

        setSelected(selected) {
            isSelected = selected;
            this.syncClasses();
        }
    }

    const overlay = new AttendanceWorksiteOverlay();
    overlay.setMap(map);
    return overlay;
}

export default {
    name: 'AttendanceWorksiteMap',
    extends: CoreLibrary,
    components: {
        AttendanceWorksiteDrawer,
    },
    data() {
        const today = window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0];

        return {
            isLoading: false,
            searchQuery: '',
            searchTimer: null,
            selectedDate: today,
            meta: {
                worksites_count: 0,
                employees_count: 0,
                total_present: 0,
                total_absent: 0,
                total_late: 0,
                total_overtime: 0,
                total_suspicious: 0,
            },
            worksites: [],
            selectedWorksiteId: null,
            drawerWorksite: null,
            map: null,
            mapViewMode: 'roadmap',
            locationLayers: {},
            isMapReady: false,
        };
    },
    computed: {
        selectedWorksite() {
            return this.worksites.find((worksite) => `${worksite.location.id}` === `${this.selectedWorksiteId}`) || null;
        },
        formattedSelectedDate() {
            const date = new Date(this.selectedDate);
            if (Number.isNaN(date.getTime())) {
                return this.selectedDate;
            }

            return new Intl.DateTimeFormat(undefined, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            }).format(date);
        },
        requestFilterPayload() {
            return {};
        },
        filterSignature() {
            return JSON.stringify({
                date: this.selectedDate,
                filters: this.requestFilterPayload,
            });
        },
        googleMapsApiKey() {
            return process.env.MIX_GOOGLE_MAPS_API_KEY || FALLBACK_GOOGLE_MAPS_KEY;
        },
    },
    mounted() {
        this.bootstrapPage();
    },
    beforeDestroy() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.clearMapLayers();
    },
    methods: {
        async bootstrapPage() {
            this.isLoading = true;

            try {
                await this.loadGoogleMaps();
                await this.$nextTick();
                this.initMap();
                await this.loadOverview();
            } catch (error) {
                this.$toastr.e(this.$t('google_maps_failed_to_load'));
            } finally {
                this.isLoading = false;
            }
        },
        async loadGoogleMaps() {
            if (window.google && window.google.maps) {
                return window.google.maps;
            }

            if (window.__mozaliGoogleMapsPromise) {
                return window.__mozaliGoogleMapsPromise;
            }

            window.__mozaliGoogleMapsPromise = new Promise((resolve, reject) => {
                const existingScript = document.getElementById('mozali-google-maps');

                if (existingScript) {
                    existingScript.addEventListener('load', () => resolve(window.google.maps));
                    existingScript.addEventListener('error', reject);
                    return;
                }

                const script = document.createElement('script');
                script.id = 'mozali-google-maps';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=geometry`;
                script.async = true;
                script.defer = true;
                script.onload = () => resolve(window.google.maps);
                script.onerror = reject;
                document.head.appendChild(script);
            });

            return window.__mozaliGoogleMapsPromise;
        },
        initMap() {
            this.map = new window.google.maps.Map(this.$refs.map, {
                center: DEFAULT_CENTER,
                zoom: 6,
                mapTypeControl: false,
                mapTypeId: this.mapViewMode,
                streetViewControl: false,
                fullscreenControl: true,
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [{ visibility: 'off' }],
                    },
                ],
            });

            this.isMapReady = true;
        },
        handleSearchInput() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            this.searchTimer = setTimeout(() => {
                this.applyFilters();
            }, 250);
        },
        buildRequestParams() {
            const params = {
                date: this.selectedDate,
            };

            if (this.searchQuery) {
                params.search = this.searchQuery;
            }

            return params;
        },
        async loadOverview() {
            this.isLoading = true;

            try {
                const response = await this.axiosGet(ATTENDANCE_WORKSITE_MAP_DATA, {
                    params: this.buildRequestParams(),
                });
                this.worksites = response.data?.locations || [];
                this.meta = response.data?.meta || this.meta;

                if (this.selectedWorksiteId) {
                    const selectedStillExists = this.worksites.find((worksite) => `${worksite.location.id}` === `${this.selectedWorksiteId}`);
                    this.selectedWorksiteId = selectedStillExists ? selectedStillExists.location.id : null;
                    this.drawerWorksite = this.drawerWorksite && selectedStillExists ? selectedStillExists : null;
                }

                if (!this.selectedWorksiteId && this.worksites.length) {
                    this.selectedWorksiteId = this.worksites[0].location.id;
                }

                this.renderMapLayers();

                if (this.selectedWorksite) {
                    this.focusWorksiteOnMap(this.selectedWorksite, false);
                } else {
                    this.fitAllWorksites();
                }
            } catch (error) {
                this.$toastr.e(this.$t('error_loading_attendance_listing'));
            } finally {
                this.isLoading = false;
            }
        },
        refreshOverview() {
            this.loadOverview();
        },
        applyFilters() {
            this.drawerWorksite = null;
            this.loadOverview();
        },
        setMapViewMode(mode) {
            if (!['roadmap', 'satellite', 'hybrid'].includes(mode)) {
                return;
            }

            this.mapViewMode = mode;
            if (this.map) {
                this.map.setMapTypeId(mode);
            }
        },
        renderMapLayers() {
            if (!this.isMapReady) {
                return;
            }

            this.clearMapLayers();

            this.worksites.forEach((worksite) => {
                if (worksite.location.center_lat === null || worksite.location.center_lng === null) {
                    return;
                }

                this.locationLayers[worksite.location.id] = this.createLocationLayers(worksite);
            });
        },
        clearMapLayers() {
            Object.values(this.locationLayers).forEach((layer) => {
                if (layer.marker?.setMap) {
                    layer.marker.setMap(null);
                }
                if (layer.geofence?.setMap) {
                    layer.geofence.setMap(null);
                }
            });

            this.locationLayers = {};
        },
        createLocationLayers(worksite) {
            const isSelected = this.selectedWorksite && `${this.selectedWorksite.location.id}` === `${worksite.location.id}`;
            const marker = createAttendanceWorksiteMarkerOverlay({
                google: window.google,
                map: this.map,
                worksite,
                isSelected,
                onClick: (selectedWorksite) => this.selectWorksite(selectedWorksite),
            });
            const geofence = this.createGeofenceOverlay(worksite.location, isSelected);

            return { marker, geofence };
        },
        createGeofenceOverlay(location, isSelected) {
            const palette = this.resolveGeofencePalette(location, isSelected);

            if (location.geofence_type === 'polygon' && location.geojson) {
                return new window.google.maps.Polygon({
                    map: this.map,
                    paths: this.geoJsonToPath(location.geojson),
                    clickable: false,
                    editable: false,
                    fillColor: palette.fillColor,
                    fillOpacity: palette.fillOpacity,
                    strokeColor: palette.strokeColor,
                    strokeOpacity: 0.95,
                    strokeWeight: palette.strokeWeight,
                });
            }

            if (location.center_lat === null || location.center_lng === null) {
                return null;
            }

            return new window.google.maps.Circle({
                map: this.map,
                center: { lat: Number(location.center_lat), lng: Number(location.center_lng) },
                radius: Number(location.radius_m || location.radius || 50),
                fillColor: palette.fillColor,
                fillOpacity: palette.fillOpacity,
                strokeColor: palette.strokeColor,
                strokeOpacity: 0.95,
                strokeWeight: palette.strokeWeight,
            });
        },
        resolveGeofencePalette(location, isSelected) {
            const baseColor = isSelected ? '#2563eb' : '#1f7a8c';
            return {
                strokeColor: baseColor,
                fillColor: baseColor,
                fillOpacity: isSelected ? 0.18 : 0.08,
                strokeWeight: isSelected ? 3 : 2,
            };
        },
        selectWorksite(worksite) {
            this.selectedWorksiteId = worksite.location.id;
            this.renderMapLayers();
            this.openWorksiteDrawer(worksite);
        },
        openWorksiteDrawer(worksite) {
            this.drawerWorksite = worksite;
        },
        closeDrawer() {
            this.drawerWorksite = null;
        },
        focusWorksiteOnMap(worksite, smoothZoom = true) {
            if (!this.map) {
                return;
            }

            const bounds = new window.google.maps.LatLngBounds();
            const location = worksite.location;

            if (location.geofence_type === 'polygon' && location.geojson) {
                this.geoJsonToPath(location.geojson).forEach((point) => bounds.extend(point));
                if (!bounds.isEmpty()) {
                    this.fitMapToBounds(bounds, FOCUS_PADDING, smoothZoom);
                    return;
                }
            }

            const layer = this.locationLayers[location.id];
            if (layer?.geofence && typeof layer.geofence.getBounds === 'function') {
                const geofenceBounds = layer.geofence.getBounds();
                if (geofenceBounds && !geofenceBounds.isEmpty()) {
                    this.fitMapToBounds(geofenceBounds, FOCUS_PADDING, smoothZoom);
                    return;
                }
            }

            if (location.center_lat !== null && location.center_lng !== null) {
                bounds.extend({ lat: Number(location.center_lat), lng: Number(location.center_lng) });
                this.fitMapToBounds(bounds, FOCUS_PADDING + 24, smoothZoom);
            }
        },
        fitAllWorksites() {
            if (!this.map || !this.worksites.length) {
                return;
            }

            const bounds = new window.google.maps.LatLngBounds();
            this.worksites.forEach((worksite) => {
                const location = worksite.location;
                if (location.center_lat !== null && location.center_lng !== null) {
                    bounds.extend({ lat: Number(location.center_lat), lng: Number(location.center_lng) });
                }
            });

            if (!bounds.isEmpty()) {
                this.fitMapToBounds(bounds, FOCUS_PADDING + 16, false);
            }
        },
        fitMapToBounds(bounds, padding = FOCUS_PADDING, smoothZoom = true) {
            if (!this.map || !bounds || bounds.isEmpty()) {
                return;
            }

            this.map.fitBounds(bounds, padding);

            window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
                if (this.map.getZoom() > MAX_FOCUS_ZOOM) {
                    this.map.setZoom(MAX_FOCUS_ZOOM);
                }
            });

            if (smoothZoom) {
                window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
                    const center = bounds.getCenter();
                    if (center) {
                        this.map.panTo(center);
                    }
                });
            }
        },
        geoJsonToPath(geojson) {
            if (!geojson || !window.google?.maps) {
                return [];
            }

            const geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
            if (!geometry || geometry.type !== 'Polygon') {
                return [];
            }

            const ring = geometry.coordinates?.[0] || [];
            return ring
                .filter((point) => Array.isArray(point) && point.length >= 2)
                .map((point) => ({ lat: Number(point[1]), lng: Number(point[0]) }))
                .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
        },
    },
};
</script>

<style>
.attendance-worksite-page__intro {
    max-width: 52rem;
}

.attendance-worksite-page__top-actions,
.attendance-worksite-page__date-nav,
.attendance-worksite-page__map-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-worksite-page__top-actions {
    justify-content: flex-end;
}

.attendance-worksite-page__top-actions--compact {
    gap: 0.75rem;
}

.attendance-worksite-page__view-switch .btn {
    min-width: 7.5rem;
}

.attendance-worksite-page__filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4rem;
    height: 1.4rem;
    margin-left: 0.45rem;
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.12);
    color: #1d4ed8;
    font-size: 0.72rem;
    font-weight: 700;
}

.attendance-worksite-page__map-shell {
    border-radius: 1rem;
}

.attendance-worksite-page__date-nav--compact {
    gap: 0.55rem;
}

.attendance-worksite-page__date-pill {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    padding: 0.7rem 1rem;
    border-radius: 999px;
    background: #f8fafc;
    min-width: 13rem;
}

.attendance-worksite-page__date-pill--compact {
    min-width: 10rem;
    padding: 0.6rem 0.85rem;
}

.attendance-worksite-page__date-pill span,
.attendance-worksite-page__label {
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
}

.attendance-worksite-page__date-pill strong {
    color: #0f172a;
}

.attendance-worksite-page__date-input {
    width: 12rem;
    border-radius: 999px;
}

.attendance-worksite-page__date-input--compact {
    width: 10.5rem;
}

.attendance-worksite-page__toolbar-search {
    display: flex;
    align-items: center;
    min-width: min(100%, 24rem);
    padding: 0 1rem;
    border: 1px solid #d9e2ec;
    border-radius: 999px;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.attendance-worksite-page__toolbar-search:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
}

.attendance-worksite-page__toolbar-search-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1rem;
    width: 1rem;
    min-width: 1rem;
    margin-right: 0.75rem;
    color: #94a3b8;
}

.attendance-worksite-page__toolbar-search-input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0 !important;
    border-radius: 0 !important;
    padding: 0.85rem 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
}

.attendance-worksite-page__toolbar-search-input:focus {
    box-shadow: none !important;
}

.attendance-worksite-page__toolbar-search-input::placeholder {
    color: #94a3b8;
}

.attendance-worksite-page__toolbar-search-icon {
    width: 1rem;
    height: 1rem;
    pointer-events: none;
}


.attendance-worksite-page__filters-panel {
    padding: 1.35rem 1.5rem;
}

.attendance-worksite-page__filters-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.attendance-worksite-page__map-shell {
    overflow: hidden;
}

.attendance-worksite-page__map-body {
    min-height: 42rem;
    padding: 1.35rem;
}

.attendance-worksite-page__map {
    width: 100%;
    min-height: 34rem;
    border-radius: 1.1rem;
    overflow: hidden;
    background: #e2e8f0;
}

.attendance-worksite-page__map-toolbar {
    margin-bottom: 1rem;
    justify-content: space-between;
}

.attendance-worksite-page__map-toolbar-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.attendance-worksite-page__map-mode-btn {
    border-color: rgba(148, 163, 184, 0.32);
    color: #475569;
    background: #fff;
}

.attendance-worksite-page__map-mode-btn.is-active {
    background: #0f172a;
    color: #fff;
    border-color: #0f172a;
}








.attendance-worksite-page__empty-state {
    min-height: 28rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    text-align: center;
    color: #64748b;
}

.attendance-worksite-page__empty-state .app-icon {
    width: 2.25rem;
    height: 2.25rem;
}




.attendance-worksite-marker-overlay {
    position: absolute;
    transform: translate(-50%, -100%);
    z-index: 100;
}

.attendance-worksite-marker-button {
    border: 0;
    background: transparent;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
}

.attendance-worksite-marker-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.42rem 0.7rem;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.92);
    color: #fff;
    font-size: 0.76rem;
    font-weight: 700;
    box-shadow: 0 12px 22px rgba(15, 23, 42, 0.22);
}

.attendance-worksite-marker-badge-dot {
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 999px;
    background: #94a3b8;
}

.attendance-worksite-marker-badge-name {
    display: inline-block;
    max-width: 11rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
}

.attendance-worksite-marker-badge-separator {
    width: 1px;
    height: 0.85rem;
    background: rgba(255, 255, 255, 0.35);
    border-radius: 999px;
}

.attendance-worksite-marker-badge-count {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
}

.attendance-worksite-marker-pin-wrap {
    position: relative;
    width: 2.8rem;
    height: 2.8rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.attendance-worksite-marker-pin {
    width: 1.45rem;
    height: 1.45rem;
    border-radius: 999px;
    border: 4px solid #fff;
    background: #94a3b8;
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.2);
}

.attendance-worksite-marker-pulse {
    position: absolute;
    inset: 0.2rem;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.28);
    animation: attendance-worksite-marker-pulse 2s ease infinite;
}

.attendance-worksite-marker-overlay.is-healthy .attendance-worksite-marker-badge-dot,
.attendance-worksite-marker-overlay.is-healthy .attendance-worksite-marker-pin {
    background: #10b981;
}

.attendance-worksite-marker-overlay.is-warning .attendance-worksite-marker-badge-dot,
.attendance-worksite-marker-overlay.is-warning .attendance-worksite-marker-pin {
    background: #f59e0b;
}

.attendance-worksite-marker-overlay.is-critical .attendance-worksite-marker-badge-dot,
.attendance-worksite-marker-overlay.is-critical .attendance-worksite-marker-pin {
    background: #ef4444;
}

.attendance-worksite-marker-overlay.is-neutral .attendance-worksite-marker-badge-dot,
.attendance-worksite-marker-overlay.is-neutral .attendance-worksite-marker-pin {
    background: #94a3b8;
}

.attendance-worksite-marker-overlay.is-selected .attendance-worksite-marker-badge {
    transform: translateY(-1px);
    box-shadow: 0 16px 30px rgba(37, 99, 235, 0.28);
}

.attendance-worksite-marker-overlay.is-selected .attendance-worksite-marker-pin {
    border-color: #dbeafe;
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
}

@keyframes attendance-worksite-marker-pulse {
    0% { transform: scale(0.95); opacity: 0.85; }
    70% { transform: scale(1.18); opacity: 0; }
    100% { transform: scale(1.18); opacity: 0; }
}


@media (max-width: 767.98px) {
    .attendance-worksite-page__map-body {
        padding: 1rem;
        min-height: 36rem;
    }

    .attendance-worksite-page__map {
        min-height: 26rem;
    }

    .attendance-worksite-page__date-nav {
        width: 100%;
    }

    .attendance-worksite-page__top-actions--compact {
        justify-content: flex-start;
    }

    .attendance-worksite-page__date-pill,
    .attendance-worksite-page__date-input,
    .attendance-worksite-page__toolbar-search {
        width: 100%;
        min-width: 100%;
    }
}
</style>
